import { DeleteOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button, Stack } from '@mui/material';
import { daysOfWeekEngOptions } from 'components/forms/fields/days-multiple-select-field/days-multiple-select-field.utils';
import SetOrderLimitsForm from 'components/forms/location/kitchen-capacity/set-order-limits-form/set-order-limits-form.component';
import { useSetOrderLimitsForm } from 'components/forms/location/kitchen-capacity/set-order-limits-form/set-order-limits-form.hooks';
import { useKitchenCapacityContext } from 'components/kitchen-capacity/kitchen-capacity.context';
import { SetOrderLimitsFormDataProps } from 'components/kitchen-capacity/order-limits-calendar/order-limits.utils';
import { useTranslation } from 'react-i18next';
import FormDrawer, {
  FormDrawerProps,
} from '../../form-drawer/form-drawer.component';
import { UtilityDrawerHookReturnType } from '../../utility-drawer/utility-drawer.hooks';

type SetOrderLimitsDrawerProps = {
  drawerData: SetOrderLimitsFormDataProps;
} & Omit<FormDrawerProps, 'children' | 'onClose' | 'formMethods' | 'onValid'> &
  UtilityDrawerHookReturnType;

const SetOrderLimitsDrawer = ({
  title,
  open,
  close,
  drawerData,
  ...rest
}: SetOrderLimitsDrawerProps) => {
  const { t } = useTranslation();
  const {
    orderLimits: { removeDummyEvent, addEvents, orderLimitsRef },
  } = useKitchenCapacityContext();
  const { form } = useSetOrderLimitsForm(drawerData);

  const finalOnValid = data => {
    try {
      removeDummyEvent();
      addEvents({
        ...data,
        daysOfWeek:
          data.daysOfWeek[0].id === 'ALL'
            ? daysOfWeekEngOptions()
            : daysOfWeekEngOptions().filter(enItem =>
                data.daysOfWeek.some(item => item.id === enItem.id),
              ),
      });

      form.reset();
      close();
    } catch (_e) {}
  };

  const cancel = () => {
    removeDummyEvent();

    form.reset();
    form.clearErrors();
    close();
  };

  return (
    <FormDrawer
      title={t('drawer.setOrderLimitsDrawer.title')}
      data-testid="SetOrderLimitsDrawer"
      open={open}
      onValid={finalOnValid}
      onClose={cancel}
      cancel={cancel}
      formMethods={form}
      buttons={(isSubmitting, isValidating) => {
        return (
          <>
            <LoadingButton
              type="submit"
              fullWidth
              loading={isSubmitting || isValidating}
              data-testid="FormDrawer.Button.Submit"
            >
              {t('drawer.setOrderLimitsDrawer.buttons.apply')}
            </LoadingButton>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                onClick={cancel}
                variant="outlined"
                data-testid="FormDrawer.Button.Cancel"
                disabled={isSubmitting || isValidating}
              >
                {t('forms.buttons.cancel')}
              </Button>
              {drawerData?.eventId && (
                <Button
                  color="error"
                  onClick={() => {
                    orderLimitsRef.current
                      .getApi()
                      .getEventById(drawerData?.eventId)
                      .remove();
                    close();
                  }}
                  data-testid="FormDrawer.Button.Delete"
                  disabled={isSubmitting || isValidating}
                  sx={{ minWidth: 'auto' }}
                  variant="outlined"
                >
                  <DeleteOutlined />
                </Button>
              )}
            </Stack>
          </>
        );
      }}
      {...rest}
    >
      {open && <SetOrderLimitsForm eventId={drawerData?.eventId} />}
    </FormDrawer>
  );
};

export default SetOrderLimitsDrawer;
